import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import {
  fromApiDirections,
  toApiDirections,
  validateDirections,
} from "../directions.js";
import { selectStations, selectTrainStopSignalImages } from "../selectors.js";
import {
  saveTrainStopSignalImage,
  deleteTrainStopSignalImage,
} from "../thunks.js";

import SignalImagesForm from "./SignalImagesForm.jsx";

const messages = {
  title: {
    id: "modules.TrainStopSignalImages.components.SignalImageEditor.title",
    description: "Signal images label",
    defaultMessage: "Machinist - Signal Images",
  },
  notFoundMessage: {
    id: "modules.TrainStopSignalImages.components.SignalImageEditor.notFoundMessage",
    description: "Error message train stop is not found",
    defaultMessage: "Couldn't find the train stop!",
  },
  deleteMessageConfirmation: {
    id: "modules.TrainStopSignalImages.components.SignalImageEditor.deleteMessageConfirmation",
    description: "Confirmation message delete signal image",
    defaultMessage: "Are you sure you want to delete this signal image?",
  },
  deleteSignalImage: {
    id: "modules.TrainStopSignalImages.components.SignalImageEditor.deleteSignalImage",
    description: "Delete signal image",
    defaultMessage: "Delete Signal Image",
  },
};

const SignalImageEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stations = useSelector(selectStations);
  const savedStations = useSelector(selectTrainStopSignalImages);
  const { identifier } = useParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const selectedStation = stations.find((s) => s.id.uicCode === identifier);
  const selectedSavedStation = savedStations.find(
    (s) => s.stopCode === identifier,
  );

  const initialValues = {
    stopCode: selectedStation?.id.code || selectedSavedStation?.stopCode || "",
    stationName:
      selectedStation?.names.long || selectedSavedStation?.stationName || "",
    signalImageRequired: selectedSavedStation?.signalImageRequired ?? true,
    directions: fromApiDirections(selectedSavedStation?.directions),
  };
  const [error, setError] = useState(null);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      const result = await dispatch(
        saveTrainStopSignalImage(
          values.stopCode,
          values.stationName,
          values.signalImageRequired,
          toApiDirections(values.directions),
        ),
      );
      if (result.status !== 200) {
        return;
      }
      navigate("/signal-image", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveSignalImage = async () => {
    try {
      await dispatch(deleteTrainStopSignalImage(selectedSavedStation.stopCode));
      navigate("/signal-image", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het verwijderen: ${err}`);
    }
  };

  const validate = (values) => {
    const errors = {};
    const required = ["stationName"];
    required.forEach((field) => {
      if (!values[field]) errors[field] = "Verplicht";
    });
    const directionErrors = validateDirections(values.directions, intl);
    if (directionErrors) errors.directions = directionErrors;
    return errors;
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)}</strong>
    );
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {intl.formatMessage(messages.title)}{" "}
        {intl.formatMessage(genericTranslations.add).toLowerCase()}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={initialValues}
          onSubmit={handleSubmit}
          formComponent={SignalImagesForm}
          validate={validate}
          disabled={error}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteSignalImage)}
          bodyText={intl.formatMessage(messages.deleteMessageConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveSignalImage}
        />
      </Card.Body>
    </Card>
  );
};

export default SignalImageEditor;
